import { Button, Flex, Form, Modal, type ModalProps, Row, message } from 'antd';
import { FC, useEffect } from 'react';

import { useAdminCompanyController_updateOneById } from '@api-client/generated/AdminCompanyController/updateOneById';
import { Schemas } from '@api-client/generated/types';
import { taxPeriodsList } from '@constants';
import { CompanyDetailsWrapperFormControl } from '@entities';
import { useCurrentCompanyId } from '@hooks';
import { captureException } from '@sentry/react';
import { getApiErrorMessage } from '@utils';

import * as S from './styled';

const SENTRY_TAGS = { feature: 'company-accounting' } as const;

type FormType = {
  details?: Pick<
    Schemas.CompanyDetailsDto,
    'vatPeriodicity' | 'financialYear' | 'firstFinancialYear' | 'bankData'
  >;
};

type CompanyAccountingEditProps = {
  company: Schemas.AdminCompanyResponseDto;
  onCancel: () => void;
  onAfterEdit: () => void;
} & ModalProps;

/**
 * The four accounting fields that master-data-service has nowhere to put.
 *
 * Everything else on the Company details card is client data and read-only, edited in Compliance
 * (which forwards its writes to MDS). These are the exception, and not by oversight: `vatPeriodicity`, `financialYear`, `firstFinancialYear`
 * and `bankData` have no column in MDS at all — they reach it only inside `extended_attributes`,
 * as provenance rebuilt from this database on every sync tick. Locking them with the rest would
 * have left them with no editor anywhere, so they stay ours until MDS grows fields for them.
 */
const CompanyAccountingEdit: FC<CompanyAccountingEditProps> = ({
  company,
  open,
  onAfterEdit,
  onCancel,
  ...rest
}) => {
  const companyId = useCurrentCompanyId();

  const [form] = Form.useForm<FormType>();

  const { mutate: updateCompany, isPending: loading } =
    useAdminCompanyController_updateOneById();

  useEffect(() => {
    form.setFieldsValue({ details: company.details });
  }, [company, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSubmit = (values: FormType) => {
    // `bankData` is merged on its own because the form holds two of its three keys: a plain
    // top-level spread replaced the stored object and silently dropped `bic`.
    const bankData = {
      ...company.details?.bankData,
      ...values.details?.bankData,
    };

    // ...and it is omitted entirely when the merge produced nothing, rather than sent as `{}`. A
    // company that has never had bank details, saved after an edit to the financial year, would
    // otherwise gain a `bankData: {}` key: "we know it is empty" written over "never filled in",
    // on a column where that distinction is the one this whole PR is careful about. An empty
    // STRING is a different matter and is kept — that is a CSM clearing a field on purpose.
    const hasBankData = Object.values(bankData).some(
      (value) => value !== undefined && value !== null
    );

    // The form's own `bankData` is pulled out of the spread, not merely overridden: rc-field-form
    // returns a shape for every registered path, so `values.details` carries
    // `bankData: { bankName: undefined, iban: undefined }` even when nothing was typed — and that
    // key would land in the payload however carefully the merged one is added afterwards.
    const { bankData: _formBankData, ...detailFields } = values.details ?? {};

    updateCompany(
      {
        parameter: {
          id: companyId!,
        },
        // `details` is a single jsonb column, so a write replaces all of it — the MDS-owned keys
        // have to be carried through rather than dropped. Nothing else about the company is sent:
        // the update is partial at column level, and the rest of the columns are not ours.
        requestBody: {
          details: {
            ...company.details,
            ...detailFields,
            ...(hasBankData ? { bankData } : {}),
          },
        },
      },
      {
        onSuccess: () => {
          message.success('Accounting details have been successfully updated');

          onAfterEdit();
          handleCancel();
        },
        // The axios interceptor only handles a 401, so without this a rejected write left the
        // modal open with no explanation — and the client-facing lock made 403 a real answer here.
        // Captured as well as shown: a toast is gone in four seconds, and a write refused for a
        // reason nobody expected (a 403 naming a field, a 500) has to leave something behind.
        onError: (error) => {
          captureException(error, { level: 'error', tags: SENTRY_TAGS });

          return message.error(
            getApiErrorMessage(
              error,
              'Could not update the accounting details.'
            )
          );
        },
      }
    );
  };

  return (
    <Modal
      width={575}
      open={open}
      title="Edit accounting details"
      footer={null}
      onCancel={handleCancel}
      destroyOnClose
      centered
      {...rest}
    >
      <Form<FormType>
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        requiredMark={false}
      >
        <S.Card>
          <Row gutter={[12, 0]}>
            <CompanyDetailsWrapperFormControl
              type="select"
              span={12}
              form={{
                label: 'VAT periodicity',
                name: ['details', 'vatPeriodicity'],
              }}
              control={{
                options: taxPeriodsList,
                allowClear: true,
              }}
            />
          </Row>

          <Row gutter={[12, 0]}>
            <CompanyDetailsWrapperFormControl
              type="input"
              span={12}
              form={{
                label: 'Financial year',
                name: ['details', 'financialYear'],
              }}
            />

            <CompanyDetailsWrapperFormControl
              type="input"
              span={12}
              form={{
                label: 'First financial year',
                name: ['details', 'firstFinancialYear'],
              }}
            />
          </Row>
        </S.Card>

        <S.Card>
          <Row gutter={[12, 0]}>
            <CompanyDetailsWrapperFormControl
              type="input"
              span={12}
              form={{
                label: 'Bank name',
                name: ['details', 'bankData', 'bankName'],
              }}
            />

            <CompanyDetailsWrapperFormControl
              type="input"
              span={12}
              form={{
                label: 'IBAN',
                name: ['details', 'bankData', 'iban'],
              }}
            />
          </Row>
        </S.Card>

        <S.Submit>
          <Form.Item noStyle>
            <Flex justify="flex-end" gap={12}>
              <Button onClick={handleCancel} size="large">
                Cancel
              </Button>

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
              >
                Save
              </Button>
            </Flex>
          </Form.Item>
        </S.Submit>
      </Form>
    </Modal>
  );
};

export default CompanyAccountingEdit;
